import { TOOL_BELT_RECORD_VERSION, TOOL_BELT_SLOTS, emptyToolBelt, nextToolSelection, parseToolBeltRecord, selectedTool, type Rules, type ToolBeltRecord, type ToolBeltSlot } from "odyssey-shared";
import type { DropTone } from "../../ui/kit/index.ts";
import { PAGE_TIMER, PENDING_TIMEOUT_MS, type BagTimer } from "../bag/snapshot.ts";

/**
 * The tool belt as the page holds it (ADR-142 decision 3b and its note of 2026-10-29): the record the server last sent
 * and the page's changes to it, drawn as the belt after the stance button and as the touch chip. Nothing changes before a
 * `toolBelt` arrives in the session, nor while the account is frozen. A change is drawn at once and the whole belt is
 * sent; one set is in flight at a time, a change made while it waits is sent with the next set once the answer comes, and
 * only the answer to the latest set replaces the page's belt with the server's. With no answer in `PENDING_TIMEOUT_MS`
 * the belt is sent again under a new seq. The seq counts from 1 and wraps past 65535 to 1. The belt is the only way to
 * hold a tool: the bar refuses one (`BarArranging.placeable`) and harvesting reads the selected slot here.
 */

/**
 * How long a line about the tool taken out or put away stays, in ms.
 */
export const NOTICE_MS = 2600;

/**
 * Sends a `toolBeltSet`: its seq, the four slots and the selection. Answers whether it was sent.
 */
export type ToolBeltSender = (seq: number, slots: readonly ToolBeltSlot[], selected: number) => boolean;

/**
 * A line the belt shows for a moment: a tool taken out, put away, or refused a place.
 */
export interface BeltNotice {
  readonly kind: "inHand" | "putAway" | "onlyTools";
  readonly item: string | null;
}

/**
 * Where a drag is held over the belt, and whether a drop there would be taken.
 */
export interface BeltOver {
  readonly index: number;
  readonly tone: DropTone;
}

/**
 * What the belt draws from.
 */
export interface BeltSnapshot {
  readonly arrived: boolean;
  readonly record: ToolBeltRecord;
  readonly dragging: number | null;
  readonly over: BeltOver | null;
  readonly notice: BeltNotice | null;
  readonly fan: boolean;
}

/**
 * The largest seq; the next after it is 1.
 */
const MAX_SEQ = 0xffff;

/**
 * Nothing arrived yet.
 */
const NOTHING: BeltSnapshot = Object.freeze({ arrived: false, record: emptyToolBelt(), dragging: null, over: null, notice: null, fan: false });

/**
 * The page's copy of the player's tool belt.
 */
export class ToolBelt {
  private snapshot: BeltSnapshot = NOTHING;
  private readonly listeners = new Set<() => void>();
  private send: ToolBeltSender = () => false;
  private seq = 0;
  private inFlight: number | null = null;
  private pending = false;
  private cancelWait: (() => void) | null = null;
  private cancelNotice: (() => void) | null = null;

  /**
   * @param rules The page's rules, which say which items are tools and name them.
   * @param frozen Whether a Game Master has frozen the account now.
   * @param timer Runs a callback after a delay, `setTimeout` on the page.
   */
  public constructor(
    private readonly rules: Rules,
    private readonly frozen: () => boolean,
    private readonly timer: BagTimer = PAGE_TIMER,
  ) {}

  /**
   * Hears each change of what the belt draws.
   *
   * @param listener Called after a change.
   * @returns Stops it.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the belt draws from now.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): BeltSnapshot => this.snapshot;

  /**
   * Names what sends a `toolBeltSet`.
   *
   * @param send The sender.
   */
  public bindSend(send: ToolBeltSender): void {
    this.send = send;
  }

  /**
   * Whether the belt may change now: a `toolBelt` arrived in this session and the account is not frozen.
   *
   * @returns True when a change would be taken.
   */
  public canArrange(): boolean {
    return this.snapshot.arrived && !this.frozen();
  }

  /**
   * The item id of the tool in hand: on the selected slot.
   *
   * @returns The id, or null when none is selected or the slot is empty.
   */
  public selectedItem(): string | null {
    return this.snapshot.arrived ? selectedTool(this.snapshot.record) : null;
  }

  /**
   * Whether an item is a tool, the only thing the belt takes.
   *
   * @param item The item's rules id.
   * @returns True for a tool.
   */
  public isTool(item: string): boolean {
    return this.rules.items.get(item)?.tool !== undefined;
  }

  /**
   * Reads a `toolBelt` from the server: a join's (seq 0) is the session's belt; an answer to the latest set in flight
   * sends the changes made meanwhile, or, with none, replaces the page's belt with the server's; any other is passed over.
   *
   * @param seq The set it answers, 0 for a join's.
   * @param slots The four slots.
   * @param selected The selection.
   */
  public receive(seq: number, slots: readonly ToolBeltSlot[], selected: number): void {
    if (seq === 0) {
      this.endWait();
      this.inFlight = null;
      this.pending = false;
      this.change({ ...this.snapshot, arrived: true, record: recordOf(slots, selected) });
      return;
    }
    if (!this.snapshot.arrived || seq !== this.inFlight) {
      return;
    }
    this.endWait();
    this.inFlight = null;
    if (this.pending) {
      this.pending = false;
      this.dispatch();
      return;
    }
    this.change({ ...this.snapshot, record: recordOf(slots, selected) });
  }

  /**
   * Puts a tool on a slot, replacing what it held.
   *
   * @param index The slot, from 0.
   * @param item The item's rules id.
   * @returns True when the belt took it: false for a belt that may not change, an item that is no tool and a slot that
   *   already holds it. An item that is no tool is answered with the notice "Only tools go on the belt."
   */
  public place(index: number, item: string): boolean {
    if (!this.canArrange() || index < 0 || index >= TOOL_BELT_SLOTS) {
      return false;
    }
    if (!this.isTool(item)) {
      this.note({ kind: "onlyTools", item: null });
      return false;
    }
    return this.edit((slots) => {
      if (slots[index] === item) {
        return null;
      }
      slots[index] = item;
      return this.snapshot.record.selected;
    });
  }

  /**
   * Puts a tool on the belt where a player did not choose a slot: the first empty slot, or slot 1 when every slot holds
   * one (the touch chip's drop, and the bag menu's Put on belt).
   *
   * @param item The item's rules id.
   * @returns True when the belt took it.
   */
  public putOn(item: string): boolean {
    const empty = this.snapshot.record.slots.findIndex((slot) => slot === null);
    return this.place(empty < 0 ? 0 : empty, item);
  }

  /**
   * Takes what a drop from the bag carries onto the belt: a slot, or, at `TOOL_BELT_SLOTS`, the chip, which takes it as
   * `putOn` does.
   *
   * @param index The slot from 0, or `TOOL_BELT_SLOTS` for the chip.
   * @param item The item's rules id.
   * @returns True when the belt took it.
   */
  public dropAt(index: number, item: string): boolean {
    this.setOver(null);
    return index === TOOL_BELT_SLOTS ? this.putOn(item) : this.place(index, item);
  }

  /**
   * Takes a tool off a slot, putting it away first when it was in hand.
   *
   * @param index The slot, from 0.
   * @returns True when the belt changed.
   */
  public clear(index: number): boolean {
    return this.edit((slots, selected) => {
      if (slots[index] === null || slots[index] === undefined) {
        return null;
      }
      slots[index] = null;
      return selected === index + 1 ? 0 : selected;
    });
  }

  /**
   * Exchanges two slots, the selection following the tool in hand.
   *
   * @param a One slot, from 0.
   * @param b The other.
   * @returns True when the belt changed.
   */
  public swap(a: number, b: number): boolean {
    return this.edit((slots, selected) => {
      const first = slots[a] ?? null;
      const second = slots[b] ?? null;
      if (a === b || first === second) {
        return null;
      }
      slots[a] = second;
      slots[b] = first;
      return selected === a + 1 ? b + 1 : selected === b + 1 ? a + 1 : selected;
    });
  }

  /**
   * Takes the tool of a slot in hand, or puts it away when it is the one in hand (a click on the selected slot). An empty
   * slot takes nothing.
   *
   * @param index The slot, from 0.
   * @returns True when the selection changed.
   */
  public toggle(index: number): boolean {
    const record = this.snapshot.record;
    if (record.slots[index] === null || record.slots[index] === undefined) {
      return false;
    }
    return this.choose(record.selected === index + 1 ? 0 : index + 1);
  }

  /**
   * Q: the selection steps through the slots that hold a tool, in order, and then to none, so with one tool Q takes it in
   * hand and puts it away; empty slots are skipped, and with no tool nothing changes.
   *
   * @returns True when the selection changed.
   */
  public cycle(): boolean {
    const { slots, selected } = this.snapshot.record;
    let next = nextToolSelection(selected);
    while (next !== 0 && slots[next - 1] === null) {
      next = nextToolSelection(next);
    }
    return this.choose(next);
  }

  /**
   * A Tool 1 to Tool 4 key: the slot's tool in hand, or put away when it is in hand already.
   *
   * @param place The slot's place from 1.
   * @returns True when the selection changed.
   */
  public press(place: number): boolean {
    return this.toggle(place - 1);
  }

  /**
   * Opens or closes the fan of slots over the chip (the touch Tool button's tap, the chip's press, Escape and a press
   * outside).
   *
   * @param open Whether the fan is open.
   */
  public setFan(open: boolean): void {
    if (this.snapshot.fan !== open) {
      this.change({ ...this.snapshot, fan: open });
    }
  }

  /**
   * Opens the fan when it is closed and closes it when it is open.
   */
  public toggleFan(): void {
    this.setFan(!this.snapshot.fan);
  }

  /**
   * Marks the slot a drag from the belt carries, or ends the mark.
   *
   * @param index The slot, or null.
   */
  public setDragging(index: number | null): void {
    if (this.snapshot.dragging !== index) {
      this.change({ ...this.snapshot, dragging: index, over: index === null ? null : this.snapshot.over });
    }
  }

  /**
   * Marks the slot a drag is held over and how a drop there would be answered, or ends the mark.
   *
   * @param over The mark, or null off the belt.
   */
  public setOver(over: BeltOver | null): void {
    const before = this.snapshot.over;
    if (before === over || (before !== null && over !== null && before.index === over.index && before.tone === over.tone)) {
      return;
    }
    this.change({ ...this.snapshot, over });
  }

  /**
   * How a belt slot answers a thing dragged from the bag: positive for a tool while the belt may change, danger for
   * anything else.
   *
   * @param item The item's rules id.
   * @returns The tone.
   */
  public toneForItem(item: string): DropTone {
    return this.canArrange() && this.isTool(item) ? "positive" : "danger";
  }

  /**
   * Forgets the session's belt as the session ends: nothing changes or is sent until the next join's `toolBelt`.
   */
  public forget(): void {
    this.endWait();
    this.endNotice();
    this.inFlight = null;
    this.pending = false;
    if (this.snapshot !== NOTHING) {
      this.change(NOTHING);
    }
  }

  /**
   * Moves the selection and says what is now in hand.
   *
   * @param selected The selection to take, 0 to `TOOL_BELT_SLOTS`.
   * @returns True when it changed.
   */
  private choose(selected: number): boolean {
    if (!this.canArrange() || selected === this.snapshot.record.selected) {
      return false;
    }
    const slots = [...this.snapshot.record.slots];
    const item = selected === 0 ? this.selectedItem() : (slots[selected - 1] ?? null);
    this.commit(slots, selected);
    this.note(item === null ? null : { kind: selected === 0 ? "putAway" : "inHand", item });
    return true;
  }

  /**
   * Applies an edit to a copy of the belt's slots and sends the result.
   *
   * @param apply Changes the slots and answers the selection to hold afterwards, or null when nothing changed.
   * @returns True when the belt changed.
   */
  private edit(apply: (slots: ToolBeltSlot[], selected: number) => number | null): boolean {
    if (!this.canArrange()) {
      return false;
    }
    const slots = [...this.snapshot.record.slots];
    const selected = apply(slots, this.snapshot.record.selected);
    if (selected === null) {
      return false;
    }
    this.commit(slots, selected);
    return true;
  }

  /**
   * Draws a belt at once and sends it, or sends it after the set in flight is answered.
   *
   * @param slots The slots.
   * @param selected The selection.
   */
  private commit(slots: ToolBeltSlot[], selected: number): void {
    this.change({ ...this.snapshot, record: { v: TOOL_BELT_RECORD_VERSION, slots, selected } });
    if (this.inFlight !== null) {
      this.pending = true;
      return;
    }
    this.dispatch();
  }

  /**
   * Sends the belt as it stands under the next seq and waits for its answer.
   */
  private dispatch(): void {
    this.seq = this.seq >= MAX_SEQ ? 1 : this.seq + 1;
    const record = this.snapshot.record;
    if (!this.send(this.seq, record.slots, record.selected)) {
      return;
    }
    this.inFlight = this.seq;
    this.cancelWait = this.timer(() => {
      this.cancelWait = null;
      this.inFlight = null;
      this.pending = false;
      if (this.snapshot.arrived) {
        this.dispatch();
      }
    }, PENDING_TIMEOUT_MS);
  }

  /**
   * Shows a line for `NOTICE_MS`, or ends the one showing.
   *
   * @param notice The line, or null for none.
   */
  private note(notice: BeltNotice | null): void {
    this.endNotice();
    this.change({ ...this.snapshot, notice });
    if (notice !== null) {
      this.cancelNotice = this.timer(() => {
        this.cancelNotice = null;
        this.change({ ...this.snapshot, notice: null });
      }, NOTICE_MS);
    }
  }

  /**
   * Stops waiting for an answer.
   */
  private endWait(): void {
    this.cancelWait?.();
    this.cancelWait = null;
  }

  /**
   * Stops the line's timer.
   */
  private endNotice(): void {
    this.cancelNotice?.();
    this.cancelNotice = null;
  }

  /**
   * Replaces the snapshot and tells every listener.
   *
   * @param next The snapshot.
   */
  private change(next: BeltSnapshot): void {
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}

/**
 * A record from the wire's slots and selection, through the shared parser; a belt it cannot read is the empty belt.
 *
 * @param slots The slots.
 * @param selected The selection.
 * @returns The record.
 */
function recordOf(slots: readonly ToolBeltSlot[], selected: number): ToolBeltRecord {
  const read = parseToolBeltRecord({ v: TOOL_BELT_RECORD_VERSION, slots, selected });
  return read.ok ? read.record : emptyToolBelt();
}
